import type { Role } from "../components/auth/AuthContext";
import { documentRoleManifestEntries } from "../pages/automation/common/documentRegistry";

/**
 * 메뉴 role 기본값 매니페스트.
 *
 * ★ 2026-09-22부터 "무엇이 메뉴인가"의 정본은 이 파일이 아니라 사이드바 코드다
 *   (menuCodeDefaults 생성물 → menuAdminTree). 이 파일은 defaultRole·표시 라벨을
 *   보태는 보조 자료다. 여기에 등록을 깜빡해도 관리 화면에는 나오고(코드 minRole 사용),
 *   여기에만 남은 옛 키는 관리 화면에 유령으로 뜨지 않는다.
 * 메뉴 구조·라벨의 정본은 각 레이아웃(CRMLayout·AutomationLayout·menuConfig)이고,
 * 여기는 "DB 매핑을 걸 수 있는 키 + 표시용 라벨 + 코드 default"의 카탈로그다.
 * 새 메뉴/섹션을 추가하면 여기에도 한 줄 추가해야 관리 UI에 뜬다(누락돼도 매핑 자체는 동작).
 * ※ /admin/* 관리 라우트는 의도적으로 제외 — 정적 super_admin 가드(자물쇠 방지).
 */
export type MenuRoleManifestEntry = {
  key: string;          // menu_role_map.menu_key
  label: string;        // 관리 UI 표시명
  defaultRole: Role;    // 코드 상수 default (매핑 없을 때)
  group: string;        // 관리 UI 그룹 헤더
};

export const MENU_ROLE_MANIFEST: MenuRoleManifestEntry[] = [
  // ── 상단 네비 ──────────────────────────────────────────────
  { key: "topnav:IT-CONSULTING", label: "상단: IT-CONSULTING", defaultRole: "viewer", group: "상단 네비" },
  { key: "topnav:CRM", label: "상단: CRM", defaultRole: "viewer", group: "상단 네비" },

  // ── CRM 사이드바 (섹션) ────────────────────────────────────
  { key: "crm:overview", label: "Overview", defaultRole: "viewer", group: "CRM 섹션" },
  { key: "crm:sales", label: "Sales", defaultRole: "viewer", group: "CRM 섹션" },
  { key: "crm:marketing", label: "Marketing", defaultRole: "viewer", group: "CRM 섹션" },
  { key: "crm:opportunity", label: "Opportunity", defaultRole: "viewer", group: "CRM 섹션" },
  { key: "crm:document-automation", label: "Document Automation", defaultRole: "viewer", group: "CRM 섹션" },
  { key: "crm:resources", label: "Resources", defaultRole: "viewer", group: "CRM 섹션" },
  { key: "crm:template", label: "Template", defaultRole: "admin", group: "CRM 섹션" },
  { key: "crm:test", label: "Test", defaultRole: "admin", group: "CRM 섹션" },

  // ── CRM 사이드바 (항목) ────────────────────────────────────
  { key: "crm:overview:dashboard", label: "Dashboard", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:overview:calendar", label: "Calendar", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:overview:automation-roadmap", label: "Automation Roadmap", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:overview:opportunity-overview", label: "Opportunity Overview", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:sales:leads", label: "Lead Inbox", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:sales:mailbox", label: "Mailbox", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:resources:company-assets", label: "회사 자료", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:resources:crm-files", label: "CRM 파일", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:sales:partners", label: "Partners", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:sales:relationships", label: "Relationships", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:marketing:marketing-dashboard", label: "Marketing Dashboard", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:marketing:event", label: "Event(참석자)", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:opportunity:pipeline", label: "Opportunities", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:sales:partner-verification", label: "Partner Verification", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:sales:partner-analysis", label: "Partner Analysis", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:opportunity:pipeline-settings", label: "Pipeline Settings", defaultRole: "viewer", group: "CRM 항목" },
  // Document Automation 문서 템플릿 — ★ 정본은 documentRegistry.ts 다(사이드바·라우트와 같은 소스).
  //    통합 작성 화면(/crm/documents/<key>)도 여기 같은 키로 보호된다 — 키를 새로 만들지 않는다.
  ...documentRoleManifestEntries().map((entry) => ({
    key: entry.key,
    label: entry.label,
    defaultRole: "viewer" as Role,
    group: "CRM 항목",
  })),
  // ★ STEP 4: 메뉴가 문서 10줄 대신 영역 입구 한 줄("문서")로 줄었다. 개별 문서 권한 키는
  //   위 파생 목록에 그대로 있고(통합 화면·구 경로가 그 키로 막힌다), 이 키는 메뉴 노출용이다.
  { key: "crm:document-automation:documents", label: "문서 템플릿", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:document-automation:3d-simulation", label: "Digital Twin Sandbox", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:document-automation:logistics-data-lab", label: "Site Data Analysis", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:template:communication-templates", label: "Template > Communication Templates", defaultRole: "viewer", group: "CRM 항목" },
  // 아래 3개는 메뉴에서 내렸지만 라우트가 살아 있어 키를 남긴다 — menu_role_map 으로
  // 다시 노출시킬 때 이 목록이 관리 화면의 선택지가 된다.
  // 사이드바에서 주석 처리만 해 둔 메뉴(CRMLayout: "주석을 풀면 된다") — 되살아날 때를 대비해 남긴다.
  // 지금은 코드에 없으므로 메뉴 관리 화면에도 뜨지 않는다.
  { key: "crm:template:lead-mail-rules", label: "Template > Lead Mail Rules (메뉴 숨김)", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:test:email-send", label: "Test > Email Send", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:test:lead-api", label: "Test > Lead API(VAULT)", defaultRole: "viewer", group: "CRM 항목" },
  { key: "crm:test:proposal-form-sample", label: "Test > 제안서 양식 샘플", defaultRole: "viewer", group: "CRM 항목" },

  // ── 문서 자동화 사이드바 (섹션) ────────────────────────────
  { key: "automation:operations", label: "운영 관리", defaultRole: "admin", group: "문서자동화 섹션" },
  { key: "automation:db", label: "DB 관리", defaultRole: "admin", group: "문서자동화 섹션" },
  { key: "automation:guides", label: "가이드", defaultRole: "viewer", group: "문서자동화 섹션" },
  { key: "automation:planning", label: "서비스 기획", defaultRole: "viewer", group: "문서자동화 섹션" },
  { key: "automation:settlement", label: "브랜드 정산", defaultRole: "admin", group: "문서자동화 섹션" },
  { key: "automation:poc", label: "POC", defaultRole: "viewer", group: "문서자동화 섹션" },
  { key: "automation:vault", label: "Vault", defaultRole: "admin", group: "문서자동화 섹션" },
  { key: "automation:equipment-catalog", label: "설비 카탈로그", defaultRole: "viewer", group: "문서자동화 섹션" },
  { key: "automation:equipment-catalog:equipment-catalog", label: "설비 카탈로그", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:vault:vault-content", label: "관리", defaultRole: "admin", group: "문서자동화 항목" },

  // ── 문서 자동화 사이드바 (항목) ────────────────────────────
  { key: "automation:operations:db-mou-status", label: "MOU 현황 관리", defaultRole: "admin", group: "문서자동화 항목" },
  { key: "automation:db:db-document-data", label: "문서 DB 관리", defaultRole: "admin", group: "문서자동화 항목" },
  { key: "automation:guides:diagnosis-logic", label: "진단 로직", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:guides:invoice-guide", label: "인보이스 가이드", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:guides:simulator", label: "시뮬레이터", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:planning:sales-pipeline", label: "Sales Pipeline", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:planning:roi-analysis", label: "ROI 분석 도구", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:settlement:brand501", label: "브랜드501", defaultRole: "admin", group: "문서자동화 항목" },
  { key: "automation:poc:warehouse-rack", label: "창고 3D Rack", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:poc:twin-studio", label: "디지털 트윈 스튜디오 (TEST)", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:poc:mwms-api-test", label: "MWMS API 테스트", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:poc:wms-billing-sim", label: "WMS Billing 시뮬레이터", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:poc:meeting-notes", label: "AI 회의록 자동화", defaultRole: "viewer", group: "문서자동화 항목" },
  { key: "automation:poc:task-management", label: "Task 관리", defaultRole: "viewer", group: "문서자동화 항목" },
];

/** 관리 UI 그룹 순서 */
export const MENU_ROLE_GROUPS = ["상단 네비", "CRM 섹션", "CRM 항목", "문서자동화 섹션"];
